/**
 * MerchantMenu — header chip showing the signed-in merchant's email plus a
 * sign-out action. Renders nothing in Studio (offline) mode since there's
 * no real auth then.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, User } from "lucide-react";
import { getApiClient } from "../lib/apiClient";

export function MerchantMenu() {
  const [merchant, setMerchant] = useState<{ email: string; display_name: string | null } | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const isOffline = getApiClient().mode === "studio";

  useEffect(() => {
    let cancelled = false;
    getApiClient()
      .auth.me()
      .then((m) => {
        if (cancelled || !m) return;
        setMerchant({ email: m.email, display_name: m.display_name });
      })
      .catch(() => {
        // Silent — RequireAuth will have already handled the no-session case.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  if (isOffline) return null;
  if (!merchant) return null;

  const label = merchant.display_name?.trim() || merchant.email;

  async function signOut() {
    setOpen(false);
    try {
      await getApiClient().auth.signout();
    } catch {
      // Server-side errors here are non-fatal — we still want to bounce the
      // user to /signin so the next request hits a fresh session check.
    }
    navigate("/signin", { replace: true });
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
      >
        <User className="w-3.5 h-3.5 text-neutral-500" />
        <span className="max-w-[180px] truncate">{label}</span>
        <ChevronDown className="w-3 h-3 text-neutral-500" />
      </button>
      {open ? (
        <div className="absolute right-0 mt-1 w-56 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 shadow-lg z-30">
          <div className="px-3 py-2 border-b border-neutral-200 dark:border-neutral-800 text-xs">
            <p className="font-medium truncate">{label}</p>
            {merchant.display_name ? (
              <p className="text-neutral-500 truncate">{merchant.email}</p>
            ) : null}
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-900 text-neutral-700 dark:text-neutral-300"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
